import React, { useState, useEffect } from 'react';
import { passwordApi, userApi } from '../../../api/adminapi';
import { MdPerson, MdEmail, MdPhone, MdBadge, MdBusiness, MdLocationOn, MdLock } from 'react-icons/md';
import './MyProfile.css';

function MyProfile() {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Change password form state
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setError('');

      // Get user info from token/localStorage
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        setError('Not authenticated. Please login again.');
        return;
      }

      // Decode JWT to get user ID
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.id;

      // Fetch user details
      const response = await userApi.getUserQRCode(userId);
      const userData = response?.data || response;

      // Extract user from UserQRResponseDTO (has structure: { user: {...}, qrCodeBase64: "..." })
      const userDetails = userData?.user || userData;
      setUserInfo(userDetails);
    } catch (err) {
      console.error('Error loading user profile:', err);
      setError('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    setPasswordError('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordForm.oldPassword.trim()) {
      setPasswordError('Current password is required');
      return;
    }

    if (!passwordForm.newPassword.trim()) {
      setPasswordError('New password is required');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.oldPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      setIsChangingPassword(true);
      await passwordApi.changePassword(passwordForm.oldPassword, passwordForm.newPassword);

      setPasswordSuccess('Password changed successfully!');

      // Reset form
      setPasswordForm({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Show success toast
      const { toast } = await import('react-toastify');
      toast.success('Password changed successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Clear success message after 5 seconds
      setTimeout(() => setPasswordSuccess(''), 5000);
    } catch (err) {
      console.error('Error changing password:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to change password';
      setPasswordError(errorMsg);

      // Show error toast
      const { toast } = await import('react-toastify');
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-view my-profile-view">
        <div className="profile-header">
          <h1>My Profile</h1>
        </div>
        <div className="loading-spinner">Loading profile...</div>
      </div>
    );
  }

  if (error && !userInfo) {
    return (
      <div className="admin-view my-profile-view">
        <div className="profile-header">
          <h1>My Profile</h1>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-view my-profile-view">
      <div className="profile-header">
        <h1>My Profile</h1>
        <p className="profile-subtitle">Manage your account information and security settings</p>
      </div>

      <div className="profile-content">
        {/* Personal Information Card */}
        <div className="profile-card">
          <div className="card-header">
            <h2><MdPerson /> Personal Information</h2>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">
                  <MdPerson className="info-icon" />
                  Full Name
                </div>
                <div className="info-value">
                  {userInfo?.firstName} {userInfo?.secondName}
                </div>
              </div>

              <div className="info-item">
                <div className="info-label">
                  <MdEmail className="info-icon" />
                  Email Address
                </div>
                <div className="info-value">{userInfo?.email}</div>
              </div>

              <div className="info-item">
                <div className="info-label">
                  <MdPhone className="info-icon" />
                  Phone Number
                </div>
                <div className="info-value">{userInfo?.phone}</div>
              </div>

              <div className="info-item">
                <div className="info-label">
                  <MdBadge className="info-icon" />
                  Employee ID
                </div>
                <div className="info-value">{userInfo?.empId}</div>
              </div>

              <div className="info-item">
                <div className="info-label">
                  <MdBusiness className="info-icon" />
                  Department
                </div>
                <div className="info-value">{userInfo?.department}</div>
              </div>

              <div className="info-item">
                <div className="info-label">
                  <MdLocationOn className="info-icon" />
                  Location
                </div>
                <div className="info-value">{userInfo?.location}</div>
              </div>

              <div className="info-item">
                <div className="info-label">
                  <MdBadge className="info-icon" />
                  Role
                </div>
                <div className="info-value">
                  <span className={`role-badge role-${userInfo?.role?.toLowerCase()}`}>
                    {userInfo?.role}
                  </span>
                </div>
              </div>

              <div className="info-item">
                <div className="info-label">
                  <MdBadge className="info-icon" />
                  Status
                </div>
                <div className="info-value">
                  <span className={`status-badge status-${userInfo?.status?.toLowerCase()}`}>
                    {userInfo?.status || 'ACTIVE'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="profile-card">
          <div className="card-header">
            <h2><MdLock /> Change Password</h2>
          </div>
          <div className="card-body">
            <form onSubmit={handleChangePassword} className="password-form">
              <div className="form-group">
                <label htmlFor="oldPassword">Current Password</label>
                <input
                  type="password"
                  id="oldPassword"
                  name="oldPassword"
                  value={passwordForm.oldPassword}
                  onChange={handlePasswordInputChange}
                  placeholder="Enter your current password"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordInputChange}
                  placeholder="Enter new password"
                  required
                />
                <div className="password-rules-hint">
                  <strong>Password Requirements:</strong>
                  <ul>
                    <li>At least 8 characters long</li>
                    <li>At least one uppercase letter (A-Z)</li>
                    <li>At least one lowercase letter (a-z)</li>
                    <li>At least one number (0-9)</li>
                    <li>At least one special character (!@#$%^&*)</li>
                    <li>Cannot contain your email, first name, or last name</li>
                    <li>Cannot reuse your last 3 passwords</li>
                  </ul>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordInputChange}
                  placeholder="Re-enter new password"
                  required
                />
              </div>

              {passwordError && (
                <div className="error-message">
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="success-message">
                  {passwordSuccess}
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={() => {
                    setPasswordForm({
                      oldPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                    setPasswordError('');
                    setPasswordSuccess('');
                  }}
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={isChangingPassword}
                >
                  {isChangingPassword ? 'Changing Password...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyProfile;
